import { SportType } from 'modules/core/domain/enums/sport-type';
import { isBoardType } from 'modules/core/domain/interfaces/supabase/overlay/auxiliary-types';
import { isMatchEventType } from 'shared/types/match-event';
import {
  BOARD_TEMPLATE_FILE_FORMAT,
  BOARD_TEMPLATE_FILE_VERSION,
  BoardTemplateFileBoard,
  BoardTemplateFileTemplate,
  DEFAULT_BOARD_DURATION,
  DEFAULT_BOARD_OBJECTS_VERSION,
  DEFAULT_BOARD_TYPE,
  DEFAULT_TEMPLATE_RESOLUTION,
} from './board-template-file';

/**
 * Why the whole file was rejected — nothing is imported from it. Unlike the media
 * manager, the template part is one per file, so its problems reject the file too.
 */
export type BoardTemplateFileError =
  | 'not-json'
  | 'wrong-format'
  | 'newer-version'
  | 'empty'
  | 'no-template-name'
  | 'unknown-sport-type'
  | 'bad-resolution';

/** Why one board of an otherwise valid file was skipped. */
export type BoardProblem = 'not-object' | 'no-name' | 'no-objects' | 'unknown-board-type' | 'duplicate-name';

export interface SkippedBoard {
  /** Position in the file's `boards`, zero-based. */
  index: number;
  name?: string;
  reason: BoardProblem;
}

export type ParsedBoardTemplateFile =
  | { error: BoardTemplateFileError }
  | { template: BoardTemplateFileTemplate; boards: BoardTemplateFileBoard[]; skipped: SkippedBoard[] };

const SPORT_TYPES: ReadonlySet<string> = new Set(Object.values(SportType));


function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isPositiveNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function trimmedName(raw: Record<string, unknown>): string {
  return typeof raw.name === 'string' ? raw.name.trim() : '';
}

function parseTemplate(raw: unknown): BoardTemplateFileTemplate | BoardTemplateFileError {
  if (!isRecord(raw)) return 'wrong-format';
  const name = trimmedName(raw);
  if (!name) return 'no-template-name';
  // A broadcast template cannot live without a sport (`Template.sportType`), and
  // guessing one would show the wrong period labels on the board.
  if (typeof raw.sportType !== 'string' || !SPORT_TYPES.has(raw.sportType)) return 'unknown-sport-type';

  let resolution = DEFAULT_TEMPLATE_RESOLUTION;
  if (raw.resolution !== undefined) {
    if (!isRecord(raw.resolution)) return 'bad-resolution';
    const { width, height } = raw.resolution;
    if (!isPositiveNumber(width) || !isPositiveNumber(height)) return 'bad-resolution';
    resolution = { width, height };
  }

  return { name, sportType: raw.sportType as SportType, resolution: { ...resolution } };
}

function parseBoard(raw: unknown, index: number): BoardTemplateFileBoard | BoardProblem {
  if (!isRecord(raw)) return 'not-object';
  const name = trimmedName(raw);
  if (!name) return 'no-name';
  if (!Array.isArray(raw.objects)) return 'no-objects';

  let type = DEFAULT_BOARD_TYPE;
  if (raw.type !== undefined) {
    // An unknown type is skipped, not coerced: a goal board turned static would
    // hang on screen for the whole match.
    if (!isBoardType(raw.type)) return 'unknown-board-type';
    type = raw.type;
  }

  // `duration` and `order` are integer columns; `duration` is seconds on screen and, as in the
  // editor (`board-duration-validator.ts`), only a positive value is valid.
  const duration = isPositiveNumber(raw.duration) && Number.isInteger(raw.duration) ? raw.duration : DEFAULT_BOARD_DURATION;
  const order = typeof raw.order === 'number' && Number.isInteger(raw.order) ? raw.order : index + 1;

  return {
    name,
    version: typeof raw.version === 'string' && raw.version ? raw.version : DEFAULT_BOARD_OBJECTS_VERSION,
    type,
    events: Array.isArray(raw.events) ? raw.events.filter(isMatchEventType) : [],
    duration,
    order,
    objects: raw.objects as BoardTemplateFileBoard['objects'],
  };
}

/**
 * Reads a broadcast template file. The file is rejected as a whole when it is not
 * ours, is from a newer format version, holds no boards or its template part is
 * broken; a broken board is skipped with a reason and its neighbours still import.
 * Unknown fields are ignored.
 */
export function parseBoardTemplateFile(text: string): ParsedBoardTemplateFile {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { error: 'not-json' };
  }

  if (!isRecord(data) || data.format !== BOARD_TEMPLATE_FILE_FORMAT || !Array.isArray(data.boards)) {
    return { error: 'wrong-format' };
  }
  if (typeof data.formatVersion !== 'number' || data.formatVersion > BOARD_TEMPLATE_FILE_VERSION) {
    return { error: 'newer-version' };
  }

  const template = parseTemplate(data.template);
  if (typeof template === 'string') return { error: template };
  if (data.boards.length === 0) return { error: 'empty' };

  const boards: BoardTemplateFileBoard[] = [];
  const skipped: SkippedBoard[] = [];
  // A board's `alias` is `concat(name, template_id)` under a unique key, so a
  // repeated name cannot be inserted — only its first board is kept.
  const seenNames = new Set<string>();
  data.boards.forEach((raw, index) => {
    let result = parseBoard(raw, index);
    if (typeof result !== 'string' && seenNames.has(result.name)) result = 'duplicate-name';
    if (typeof result === 'string') {
      const name = isRecord(raw) ? trimmedName(raw) : '';
      skipped.push(name ? { index, name, reason: result } : { index, reason: result });
      return;
    }
    seenNames.add(result.name);
    boards.push(result);
  });
  return { template, boards, skipped };
}
